// src/components/ui/Button/Button.tsx
import React, {useState} from 'react';
import {IonButton, IonIcon, IonSpinner} from '@ionic/react';
import type {ComponentProps} from 'react';

// ═══════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════

/** Button semantic variants aligned with brand guidelines */
export type ButtonVariant =
    | 'primary'      // Foundry Blue - Structure
    | 'cta'          // Forge Gold - Action (use sparingly!)
    | 'success'      // Timberline Green - Growth
    | 'danger';      // Expense Red

/** Extended IonButton props with brand enhancements */
export interface ButtonProps extends Omit<ComponentProps<typeof IonButton>, 'color'> {
    /** Semantic variant - determines color scheme */
    variant?: ButtonVariant;
    /** Icon to display before text */
    iconStart?: string;
    /** Icon to display after text */
    iconEnd?: string;
    /** Show loading spinner */
    loading?: boolean;
    /** Force hover state (for previews/demos) */
    forceHover?: boolean;
    /** Children content */
    children?: React.ReactNode;
}

// ═══════════════════════════════════════════════════════════════════
// VARIANT TO IONIC COLOR MAPPING
// ═══════════════════════════════════════════════════════════════════

const variantToColor: Record<ButtonVariant, string> = {
    primary: 'primary',    // Maps to --ion-color-primary (Foundry Blue)
    cta: 'secondary',      // Maps to --ion-color-secondary (Forge Gold)
    success: 'success',    // Maps to --ion-color-success (Timberline Green)
    danger: 'danger',      // Maps to --ion-color-danger (Red)
};

// ═══════════════════════════════════════════════════════════════════
// BUTTON COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    iconStart,
    iconEnd,
    loading = false,
    disabled = false,
    fill = 'solid',
    size,
    expand,
    forceHover = false,
    children,
    style,
    className,
    onMouseEnter,
    onMouseLeave,
    ...props
}) => {
    const [isHovered, setIsHovered] = useState(false);

    // Combine actual hover with forced hover
    const showHoverState = (isHovered || forceHover) && !disabled && !loading;

    const ionColor = variantToColor[variant];

    // Enhanced hover effect for solid buttons
    const getTransform = () => {
        if (disabled || loading) return 'none';
        if (showHoverState && fill === 'solid') return 'translateY(-1px)';
        return 'none';
    };

    // Enhanced shadow for solid CTA buttons on hover
    const getBoxShadow = () => {
        if (disabled || loading || fill !== 'solid') return undefined;
        if (variant === 'cta' && showHoverState) {
            return '0 6px 20px rgba(203, 160, 82, 0.45)';
        }
        if (variant === 'primary' && showHoverState) {
            return '0 6px 20px rgba(27, 59, 90, 0.4)';
        }
        if (variant === 'success' && showHoverState) {
            return '0 6px 20px rgba(46, 90, 54, 0.4)';
        }
        if (variant === 'danger' && showHoverState) {
            return '0 6px 20px rgba(220, 38, 38, 0.4)';
        }
        return undefined;
    };

    const handleMouseEnter = (e: React.MouseEvent<HTMLIonButtonElement>) => {
        setIsHovered(true);
        onMouseEnter?.(e);
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLIonButtonElement>) => {
        setIsHovered(false);
        onMouseLeave?.(e);
    };

    // Note: Clear/outline primary button colors are handled via CSS in variables.css
    // Dark mode: White text (#FFFFFF) with Forge Gold hover (#CBA052)
    // Light mode: Foundry Blue text with Forge Gold hover

    const buttonStyle: React.CSSProperties = {
        '--box-shadow': getBoxShadow(),
        transition: 'all 0.2s ease',
        transform: getTransform(),
        ...style,
    } as React.CSSProperties;

    // Add hover preview class for clear/outline buttons when hover state is forced
    const isClearOrOutline = fill === 'clear' || fill === 'outline';
    const hoverClass = showHoverState && isClearOrOutline ? 'button-hover-preview' : '';
    const combinedClassName = [className, hoverClass].filter(Boolean).join(' ') || undefined;

    return (
        <IonButton
            color={ionColor}
            fill={fill}
            className={combinedClassName}
            size={size}
            expand={expand}
            disabled={disabled || loading}
            style={buttonStyle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            {...props}
        >
            {loading ? (
                <IonSpinner
                    name="crescent"
                    slot="start"
                    style={{width: 18, height: 18}}
                />
            ) : iconStart ? (
                <IonIcon slot="start" icon={iconStart} />
            ) : null}

            {children}

            {!loading && iconEnd && (
                <IonIcon slot="end" icon={iconEnd} />
            )}
        </IonButton>
    );
};

Button.displayName = 'Button';

export default Button;
